import { EntityKind, type EntityState } from "odyssey-shared";
import type { RoutedKey } from "../../ui/input/router.ts";
import type { SceneCounts } from "./monitor.ts";
import { pageKeys, type KeyTable } from "../controls/page.ts";
import { actionPress } from "../controls/press.ts";

/**
 * The performance panel's ties to the page (ADR-087 decisions 2 and 3): what it counts in the world scene, and F9
 * through the input router.
 */

/**
 * What the counts read of the world scene: its display list, its prop views, the current connection's client view
 * and the player's own handle.
 */
export interface CountedScene {
  readonly children: { readonly length: number };
  readonly props: ReadonlyMap<string, { readonly count: number }>;
  readonly connection: { readonly view: { readonly entities: ReadonlyMap<number, EntityState> } } | null;
  readonly handle: number;
}

const NOTHING: SceneCounts = { objects: 0, props: 0, players: 0, creatures: 0, things: 0 };

/**
 * The scene's counts at one update: its game objects, the props its prop views hold, and the other players, the
 * creatures (NPCs counted with them, as the map server's perf report counts them) and the things in the client view.
 *
 * @param scene The world scene, or null before the page has made it.
 * @returns The counts, every one 0 without a scene.
 */
export function sceneCounts(scene: CountedScene | null): SceneCounts {
  if (scene === null) {
    return NOTHING;
  }
  let props = 0;
  for (const view of scene.props.values()) {
    props += view.count;
  }
  let players = 0;
  let creatures = 0;
  let things = 0;
  for (const [handle, entity] of scene.connection?.view.entities ?? []) {
    if (handle === scene.handle) {
      continue;
    }
    if (entity.kind === EntityKind.Player) {
      players += 1;
    } else if (entity.kind === EntityKind.Creature || entity.kind === EntityKind.Npc) {
      creatures += 1;
    } else {
      things += 1;
    }
  }
  return { objects: scene.children.length, props, players, creatures, things };
}

/**
 * Binds the key the key table gives Performance (F9 by default; P1.9-T50, `controls/`) to the panel through the input
 * router, so only while the game owns the keyboard (never while a text field has focus or a modal holds the keys); an
 * auto-repeat, or a press with Ctrl, Alt or Meta, is left alone.
 *
 * @param monitor What the key toggles.
 * @param input The page's input router.
 * @param keys The key table; the page's unless given.
 * @returns A function that unbinds it.
 * @example
 * bindPerfKey(perf, input);
 */
export function bindPerfKey(monitor: { readonly toggle: () => void }, input: { readonly onKey: (listener: (event: RoutedKey) => void) => () => void }, keys: Pick<KeyTable, "actionOf"> = pageKeys): () => void {
  return input.onKey((event) => {
    if (!actionPress(keys, "performance", event) || event.repeat || event.ctrlKey === true || event.altKey === true || event.metaKey === true || event.defaultPrevented) {
      return;
    }
    event.preventDefault();
    monitor.toggle();
  });
}
